import React, { useCallback, useEffect, useState } from "react";
import { Button, ConfigProvider, Modal, Select, Spin, Table, Upload, message } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import style from "css/admin/component.module.css";
import axios from "axios";

const { Dragger } = Upload;

function MemberUploadModal({ open, onCancel, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [campusCd, setCampusCd] = useState(undefined);
  const [campusOptions, setCampusOptions] = useState([]);

  useEffect(() => {
    if (!open) return;
    axios
      .get("/code", { params: { GROUP_CD: "CD06" } })
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) {
          setCampusOptions(
            res.data.map((c) => ({ value: c.CODE_CD, label: c.VIEW_NM }))
          );
        }
      })
      .catch(() => setCampusOptions([]));
  }, [open]);

  const closeHandler = () => {
    setFile(null);
    setResult(null);
    setCampusCd(undefined);
    onCancel();
  };

  const beforeUpload = useCallback((f) => {
    const isExcel = f.name?.endsWith(".xlsx") || f.name?.endsWith(".xls");
    if (!isExcel) {
      message.error("엑셀 파일만 업로드 가능합니다. (xls, xlsx)");
      return Upload.LIST_IGNORE;
    }
    setFile(f);
    setResult(null);
    return false;
  }, []);

  const onRemove = useCallback(() => {
    setFile(null);
    setResult(null);
  }, []);

  const handleUpload = async () => {
    if (!campusCd) {
      message.warning("캠퍼스를 선택해주세요.");
      return;
    }
    if (!file) {
      message.warning("파일을 선택해주세요.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("CAMPUS_CD", campusCd);

    try {
      setLoading(true);
      const res = await axios.post("/user/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.status === 200 && res.data) {
        const d = res.data;
        if (d.message === "INVALID_HEADER") {
          message.error("엑셀 헤더가 양식과 일치하지 않습니다. 양식을 다운로드하여 확인해주세요.");
        } else if (d.message === "PARSE_ERROR") {
          message.error("엑셀 파일 파싱 중 오류가 발생했습니다.");
        } else {
          setResult(d);
          message.success(
            `처리 완료 (총 ${d.total || 0}건: 등록 ${d.insertCnt || 0}, 갱신 ${d.updateCnt || 0}, 실패 ${d.failCnt || 0})`
          );
          if ((d.failCnt || 0) === 0) {
            if (typeof onSuccess === "function") onSuccess(d);
          }
        }
      }
    } catch (e) {
      const msg = e?.response?.data ?? e?.message ?? "업로드 중 오류가 발생했습니다.";
      message.error(String(msg));
    } finally {
      setLoading(false);
    }
  };

  const errorColumns = [
    {
      title: "행 번호",
      dataIndex: "row",
      key: "row",
      width: 80,
      align: "center",
      render: (v) => <span className={style.table_cell}>{v}</span>,
    },
    {
      title: "오류 내용",
      dataIndex: "message",
      key: "message",
      render: (v) => <span className={style.table_cell_error}>{v || "-"}</span>,
    },
  ];

  return (
    <Modal
      title={null}
      open={open}
      onCancel={closeHandler}
      closable={false}
      maskClosable={!loading}
      footer={
        <ConfigProvider
          theme={{
            token: { colorPrimary: "#527ff2" },
            components: {
              Button: {
                colorPrimaryHover: "#4a72de",
                colorPrimaryActive: "#3f66c8",
              },
            },
          }}
        >
          <div className={style.modal_footer_wrap}>
            <Button
              key="cancel"
              onClick={closeHandler}
              disabled={loading}
              className={style.modal_footer_btn}
            >
              취소
            </Button>
            <Button
              key="submit"
              type="primary"
              onClick={handleUpload}
              loading={loading}
              disabled={!file || !campusCd}
              className={style.modal_footer_btn}
            >
              업로드
            </Button>
          </div>
        </ConfigProvider>
      }
      styles={{ content: { borderRadius: 12 }, mask: { backdropFilter: "none" } }}
      width={640}
      centered
    >
      <Spin spinning={loading}>
        <div style={{ display: "flex", flexDirection: "column", gap: 20, padding: "8px 0" }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 20, fontWeight: 700, fontFamily: "Pretendard", color: "#33353b", marginBottom: 8 }}>
              인사정보 엑셀 업로드
            </div>
            <div style={{ fontSize: 14, fontFamily: "Pretendard", color: "#797d88", lineHeight: 1.6 }}>
              엑셀을 업로드하여 사용자 인사정보를 일괄 등록/갱신할 수 있습니다.<br />
              정해진 엑셀 양식에 입력하여 업로드하세요. (지원 파일 양식: xls, xlsx)
            </div>
            <span
              style={{ fontSize: 14, fontFamily: "Pretendard", color: "#527ff2", textDecoration: "underline", marginTop: 4, display: "inline-block", cursor: "pointer" }}
              onClick={() => {
                axios.get("/user/template", { responseType: "blob" })
                  .then(res => {
                    const url = window.URL.createObjectURL(new Blob([res.data]));
                    const link = document.createElement("a");
                    link.href = url;
                    link.setAttribute("download", "인사정보_양식.xlsx");
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                  })
                  .catch(() => {
                    message.error("양식 다운로드에 실패했습니다.");
                  });
              }}
            >
              엑셀 양식 다운로드
            </span>
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12 }}>
            <Select
              placeholder="캠퍼스를 선택하세요"
              value={campusCd}
              onChange={(v) => setCampusCd(v)}
              options={campusOptions}
              style={{ width: 240, height: 40 }}
              size="large"
            />
          </div>

          <Dragger
            beforeUpload={beforeUpload}
            onRemove={onRemove}
            fileList={file ? [file] : []}
            maxCount={1}
            accept=".xls,.xlsx"
            style={{ borderRadius: 8 }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined style={{ fontSize: 36, color: "#527ff2" }} />
            </p>
            <p style={{ fontSize: 14, fontFamily: "Pretendard", color: "#797d88" }}>
              클릭 또는 드래그해서 파일을 선택해 주세요.
            </p>
          </Dragger>

          {result && (
            <div>
              <div className={style.result_summary} style={{ marginBottom: 8 }}>
                <span>총 <b>{result.total || 0}</b>건</span>
                <span style={{ color: "#527ff2" }}>등록 <b>{result.insertCnt || 0}</b>건</span>
                <span style={{ color: "#33353b" }}>갱신 <b>{result.updateCnt || 0}</b>건</span>
                <span style={{ color: "#f65c22" }}>실패 <b>{result.failCnt || 0}</b>건</span>
              </div>

              {Array.isArray(result.errors) && result.errors.length > 0 && (
                <Table
                  rowKey={(row) => row?.row}
                  columns={errorColumns}
                  dataSource={result.errors}
                  size="small"
                  pagination={{ pageSize: 5, showSizeChanger: false }}
                  scroll={{ y: 200 }}
                />
              )}
            </div>
          )}
        </div>
      </Spin>
    </Modal>
  );
}

export default MemberUploadModal;
